import React, { useState, useMemo } from "react";
import { FaEye, FaDownload, FaSearch, FaFilter, FaBroom } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface Application {
  id: number;
  companyName: string;
  positionProgram: string;
  dateApplied: string;
  applicationStatus: string;
  feedbackReceived: string;
  nextAction: string;
  nextActionDue: string;
  contactPerson: string;
  contactEmail: string;
  notes: string;
  coverLetterDownloadUrl?: string;
}

interface StudentOverview {
  studentId: number;
  studentName: string;
  studentEmail: string;
  isActivated: boolean;
  totalApplications: number;
  todayApplicationsCount: number;
  todayTargetMet: boolean;
  missedTargetDaysCount: number;
  missedTargetDates: string[];
  allApplications: Application[];
}

interface StudentDetailsModalProps {
  student: StudentOverview;
  onClose: () => void;
}

const StudentDetailsModal: React.FC<StudentDetailsModalProps> = ({
  student,
  onClose,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [feedbackFilter, setFeedbackFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  // Get unique statuses and feedback types
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(
      student.allApplications.map((app) => app.applicationStatus),
    );
    return Array.from(statuses).sort();
  }, [student.allApplications]);

  const uniqueFeedbacks = useMemo(() => {
    const feedbacks = new Set(
      student.allApplications.map((app) => app.feedbackReceived),
    );
    return Array.from(feedbacks).sort();
  }, [student.allApplications]);

  // Filter applications based on search and filters
  const filteredApplications = useMemo(() => {
    let filtered = student.allApplications;

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.companyName.toLowerCase().includes(search) ||
          app.positionProgram.toLowerCase().includes(search) ||
          app.contactPerson?.toLowerCase().includes(search) ||
          app.contactEmail?.toLowerCase().includes(search) ||
          app.notes?.toLowerCase().includes(search),
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (app) => app.applicationStatus === statusFilter,
      );
    }

    // Feedback filter
    if (feedbackFilter !== "all") {
      filtered = filtered.filter(
        (app) => app.feedbackReceived === feedbackFilter,
      );
    }

    return filtered;
  }, [student.allApplications, searchTerm, statusFilter, feedbackFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = filteredApplications.slice(
    startIndex,
    endIndex,
  );

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, feedbackFilter, itemsPerPage]);

  // Reset to page 1 when itemsPerPage changes
  const handleItemsPerPageChange = (newValue: number) => {
    setItemsPerPage(newValue);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setFeedbackFilter("all");
  };

  const hasActiveFilters =
    searchTerm || statusFilter !== "all" || feedbackFilter !== "all";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{student.studentName}</h2>
            <p className="text-cyan-100 text-sm">{student.studentEmail}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 180px)" }}
        >
          {/* Student Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">
                Total Applications
              </p>
              <p className="text-2xl font-bold text-blue-700">
                {student.totalApplications}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-sm text-purple-600 font-medium">
                Today's Applications
              </p>
              <p className="text-2xl font-bold text-purple-700">
                {student.todayApplicationsCount} / 2
              </p>
            </div>
            <div
              className={`rounded-lg p-4 border ${
                student.todayTargetMet
                  ? "bg-green-50 border-green-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  student.todayTargetMet ? "text-green-600" : "text-yellow-600"
                }`}
              >
                Today's Status
              </p>
              <p
                className={`text-2xl font-bold ${
                  student.todayTargetMet ? "text-green-700" : "text-yellow-700"
                }`}
              >
                {student.todayTargetMet ? "Met" : "Not Met"}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm text-red-600 font-medium">Missed Days</p>
              <p className="text-2xl font-bold text-red-700">
                {student.missedTargetDaysCount}
              </p>
            </div>
          </div>

          {/* Applications Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Search and Filter Bar */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">
                  All Applications
                </h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                >
                  <FaFilter />
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
              </div>

              {/* Search Bar */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 relative">
                  <FaSearch className="absolute left-3 top-3 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search by company, position, contact, or notes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                  />
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                    title="Clear all filters"
                  >
                    <FaBroom />
                    Clear
                  </button>
                )}
              </div>

              {/* Filter Dropdowns */}
              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Application Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                    >
                      <option value="all">All Statuses</option>
                      {uniqueStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Feedback Received
                    </label>
                    <select
                      value={feedbackFilter}
                      onChange={(e) => setFeedbackFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                    >
                      <option value="all">All Feedback Types</option>
                      {uniqueFeedbacks.map((feedback) => (
                        <option key={feedback} value={feedback}>
                          {feedback}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Results Count and Items Per Page */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                <span>
                  Showing {startIndex + 1}-
                  {Math.min(endIndex, filteredApplications.length)} of{" "}
                  {filteredApplications.length}
                  {hasActiveFilters &&
                    student.allApplications.length !==
                      filteredApplications.length && (
                      <span className="text-gray-500">
                        {" "}
                        (filtered from {student.allApplications.length})
                      </span>
                    )}
                </span>
                <div className="flex items-center gap-2">
                  <label>Per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Company
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Position
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date Applied
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Cover Letter
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Feedback
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Next Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedApplications.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        {hasActiveFilters ? (
                          <div>
                            <p className="font-medium mb-2">
                              No matching applications found
                            </p>
                            <button
                              onClick={resetFilters}
                              className="text-sm text-[#009fe3] hover:text-[#007cb5] font-medium"
                            >
                              Clear filters to see all applications
                            </button>
                          </div>
                        ) : (
                          "No applications yet"
                        )}
                      </td>
                    </tr>
                  ) : (
                    paginatedApplications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {app.companyName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {app.positionProgram}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(app.dateApplied).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {app.coverLetterDownloadUrl ? (
                            <div className="flex gap-2">
                              <a
                                href={app.coverLetterDownloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-xs font-medium"
                              >
                                <FaEye size={12} /> Preview
                              </a>
                              <a
                                href={app.coverLetterDownloadUrl}
                                download
                                className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 text-xs font-medium"
                              >
                                <FaDownload size={12} /> Download
                              </a>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs italic">
                              No file
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {app.applicationStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {app.feedbackReceived}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {app.nextAction}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredApplications.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  {totalPages <= 7 ? (
                    Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 border rounded-md text-sm font-medium ${
                            currentPage === page
                              ? "bg-[#009fe3] text-white border-[#009fe3]"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {page}
                        </button>
                      ),
                    )
                  ) : (
                    <>
                      {currentPage > 3 && (
                        <span className="text-gray-500">...</span>
                      )}
                      {Array.from({ length: 5 }, (_, i) => {
                        const page = currentPage - 2 + i;
                        if (page < 1 || page > totalPages) return null;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 border rounded-md text-sm font-medium ${
                              currentPage === page
                                ? "bg-[#009fe3] text-white border-[#009fe3]"
                                : "border-gray-300 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      {currentPage < totalPages - 2 && (
                        <span className="text-gray-500">...</span>
                      )}
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsModal;
